import { FormSectionHandler } from '../entity.handler';
import { DeviceTypeResponse, DeviceTypeUpdateRequest, DeviceTypeIOPropertiesDictionary } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, SectionField } from '../utils';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import { IdName } from 'app/shared/graphql';


export type InputType = 'analogs' | 'cameras' | 'cans' | 'counters' | 'digitals' | 'outputs' | 'temperatures';
export const InputTypeValues: InputType[] = ['analogs', 'cameras', 'cans', 'counters', 'digitals', 'outputs', 'temperatures'];

const InputTypeIcons = {
    analogs: 'tachometer',
    cameras: 'camera-alt',
    cans: 'oil-can',
    counters: 'abacus',
    digitals: 'arrow-to-left',
    outputs: 'arrow-from-left',
    temperatures: 'thermometer-half',
};

const InputTypeIoType = {
    analogs: 'analog_input',
    cameras: 'camera_input',
    cans: 'can_input',
    counters: 'counter_input',
    digitals: 'digital_input',
    outputs: 'digital_output',
    temperatures: 'temperature_input',
};


interface EditorValues {
    items: ListFieldValue<IdName>[];
}


export class DeviceTypeIoSectionHandler implements FormSectionHandler<DeviceTypeResponse, EditorValues> {

    type = 'form';
    icon = InputTypeIcons[this.inputType];
    bulk = false;
    hidden = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    ioKey = InputTypeIoType[this.inputType];

    
    modifiedInputs = {};

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public inputType: InputType
    ) {

    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.DEVICETYPE.IO_${this.inputType.toUpperCase()}.${key}`, values);
    }

    applyUpdate(entity: DeviceTypeResponse, payload: DeviceTypeUpdateRequest): Promise<DeviceTypeResponse> {
        return this.admin.applyUpdate(api => api.entities.updateDeviceType(entity.id, payload));
    }

    async mapEntityToEditor(entity: DeviceTypeResponse): Promise<EditorValues> {
        this.modifiedInputs = {};
        const obj = ((entity.ioCapabilities || {})[InputTypeIoType[this.inputType]] || {}) as DeviceTypeIOPropertiesDictionary;
        return {
            items: Object.keys(obj).map(key => this.ioItemToListItem({ id: key, name: key })),
        };
    }

    async mapEditorToEntity(entity: DeviceTypeUpdateRequest, _values: EditorValues): Promise<DeviceTypeUpdateRequest> {
        const obj = (entity.ioCapabilities || { [InputTypeIoType[this.inputType]]: {} });
        Object.keys(this.modifiedInputs).filter(key => this.modifiedInputs[key] === 'deleted').forEach(item => {
            obj[InputTypeIoType[this.inputType]][item] = null;
        });
        Object.keys(this.modifiedInputs).filter(key => this.modifiedInputs[key] === 'added').forEach(item => {
            obj[InputTypeIoType[this.inputType]][item] = {};
        });
        return { ioCapabilities: obj };
    }

    getFields(entity: DeviceTypeResponse): Promise<SectionField[]> {
        return Promise.resolve([
            {
                id: 'items', title: this.admin.translate(`EDITORS.DEVICETYPE.IO.FIELDS.ITEMS`), type: 'list', required: false,
                actions: [
                    { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showAddInputModal.bind(this), },
                    { id: 'delete', click: this.markDeletedItem.bind(this) },
                ],
                getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
            },
        ]).then(fields => EntityUtils.prepareBulk(!entity, [], fields));
    }

    async markDeletedItem(_action: any, _field: FormBuilderField, _values: any, item?: ListFieldValue<IdName>): Promise<true> {
        this.modifiedInputs[item.id] = 'deleted';
        return true;
    }    

    showAddInputModal(_action: string, _field: FormBuilderField, values: EditorValues): Promise<IdName> {

        const translate = (key: string, vals?: Object): string => {
            return this.admin.translate(`EDITORS.DEVICETYPE.IO.${key}`, vals);
        };
    

        return this.admin.showFormModal({
            groups: [{
                name: translate('MODAL.TITLE', { type: this.inputType }),
                description: translate('MODAL.DESC'),
                fields: [
                    { id: 'value', title: translate('MODAL.FIELDS.VALUE'), type: 'text', min: 1, max: 32, required: true },
                ],
            }],
        }, { value: '' }
        ).then(result => {
            if (result) {
                result.value = result.value.toLowerCase().replace(/[^a-z0-9_]/ig, '_');
                values.items.push(this.ioItemToListItem({ id: result.value, name: result.value }));
                this.modifiedInputs[result.value] = 'added';
               
            }
            return null;
        });


    }    

    ioItemToListItem(item: IdName): ListFieldValue<IdName> {
        return {
            id: item.id,
            name: item.name,
            icon: InputTypeIcons[this.inputType],
            data: item,
        };
    }    


}
